import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import HTMLFlipBook from "react-pageflip";
import Axios from "axios";
import BannerSection from "../section/BannerSectionWFull";
import LazyImage from "./LazyImage";

//import LoadingSpinner from "../Cms/Layout/Spinner";

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassPlusIcon,
  MagnifyingGlassMinusIcon,
  HomeIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

/* ───────── ขนาดการแสดงผลโบรชัวร์ (responsive) ─────────
   PAGE_RATIO   สัดส่วนหน้ากระดาษ กว้าง/สูง — วัดจากไฟล์จริงที่ API เสิร์ฟ (594 × 846)
   TOP_BAR_H     แถบหัว (ชื่อเล่ม) h-[52px] + progress bar 3px ที่ติดหัวจอ — มีเฉพาะ Mode 2
                 (Mode 1 เล่มต่อใต้ banner เลย)
   TOOLBAR_H     แถบปุ่มลอย (ปุ่ม 44px + padding 8 + เว้นจากท้ายเล่ม 12) กันไม่ให้ทับท้ายเล่ม
   TOOLBAR_GAP_* ระยะแถบปุ่มลอยจาก footer (Mode 1) / จากขอบจอ (Mode 2 ไม่มี footer)
   FOOTER_H      Footer ใน App.js เป็น fixed bottom-0 h-14 จึงทับหนังสือถ้าไม่กันที่ไว้
                 (แบนเนอร์ด้านบนไม่ต้องนับ เพราะเลื่อนพ้นจอไปตอนอ่าน)
                 Mode 2 ซ่อน footer จึงส่ง 0 เข้า calcBookSize แทน
   SPREAD_MIN_W ต่ำกว่านี้แสดงหน้าเดียว — หน้าคู่บนจอ 700–900px ทำให้แต่ละหน้าแคบกว่าเดิม
   MAX_PAGE_W   เพดานกันไม่ให้บานเกินบนจอ 4K
   PAD_*        ระยะขอบซ้าย/ขวา หน้าคู่ต้อง ≥ 64 เพราะปุ่มกลมวางนอกเล่ม (-left-[60px])
                 หน้าเดียวปุ่มลอยทับขอบเล่มอยู่แล้ว */
const PAGE_RATIO = 594 / 846;
const TOP_BAR_H = 55;
const TOOLBAR_H = 64;
const TOOLBAR_GAP_FOOTER = 50;
const TOOLBAR_GAP_EDGE = 12;
const FOOTER_H = 56;
const SPREAD_MIN_W = 900;
const MAX_PAGE_W = 1000;
const PAD_PORTRAIT = 12;
const PAD_SPREAD = 68;

// เครื่องที่เคยกด "เข้าใจแล้ว" / เคยพลิกหน้าแล้ว จะไม่เห็นหน้าแนะนำการปัดอีก
const SWIPE_GUIDE_KEY = "catalog_swipe_guide_seen";
function readGuideSeen() {
  try {
    return localStorage.getItem(SWIPE_GUIDE_KEY) === "1";
  } catch {
    return false;
  }
}

// "DD/MM/YYYY HH:mm:ss" จาก API → "8 ก.ค. 2569"
function formatThaiDate(value) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(value || "");
  if (!m) return "";
  return new Date(+m[3], +m[2] - 1, +m[1]).toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// safe-area ล่างของ iPhone อ่านค่าจริงจาก env() แทนการเดา
function readSafeBottom() {
  if (typeof document === "undefined") return 0;
  try {
    const probe = document.createElement("div");
    probe.style.cssText =
      "position:fixed;bottom:0;left:0;width:0;height:env(safe-area-inset-bottom);visibility:hidden;pointer-events:none";
    document.body.appendChild(probe);
    const h = probe.getBoundingClientRect().height || 0;
    probe.remove();
    return h;
  } catch {
    return 0;
  }
}

/* ───────── display_mode ของ catalog (ตั้งใน CMS / oneManage) ─────────
   1 = แสดง banner + footer (ค่าเริ่มต้น)
   2 = ซ่อน banner + footer
   ?mode=1|2 ในลิงก์ใช้แทนค่าจาก API ได้ (ไว้เทส / ทำลิงก์พิเศษ) */
const DISPLAY_MODE_SHOW = 1;
const DISPLAY_MODE_HIDE = 2;

function resolveDisplayMode(modeQuery, catalogMode) {
  const fromQuery = parseInt(modeQuery, 10);
  if (fromQuery === DISPLAY_MODE_SHOW || fromQuery === DISPLAY_MODE_HIDE) {
    return fromQuery;
  }
  return Number(catalogMode) === DISPLAY_MODE_HIDE
    ? DISPLAY_MODE_HIDE
    : DISPLAY_MODE_SHOW;
}

// reservedH = ความสูงที่ถูกกินไปด้วยแถบหัว / แถบปุ่มลอย / footer (ไม่รวม banner)
function calcBookSize(vw, vh, safeBottom = 0, reservedH = 0) {
  const portrait = vw < SPREAD_MIN_W;
  const pad = portrait ? PAD_PORTRAIT : PAD_SPREAD;
  const chrome = reservedH + safeBottom;
  const availH = Math.max(300, vh - chrome);
  const availW = Math.min(vw, 2000) - pad * 2;

  // หน้าคู่: ความสูงจอเป็นตัวบีบ · หน้าเดียว: ความกว้างจอเป็นตัวบีบ
  let w = portrait ? availW : availW / 2;
  w = Math.min(w, availH * PAGE_RATIO);
  w = Math.max(260, Math.min(w, MAX_PAGE_W));

  return {
    portrait,
    pad,
    width: Math.round(w),
    height: Math.round(w / PAGE_RATIO),
  };
}

const clientsThis = async (headers) => {
  try {
    const res = await Axios.get(
      `${process.env.REACT_APP_API_URL}/api/clients/this`,
      { headers }
    );
    return res.data?.data?.variables;
  } catch {
    return "";
  }
};

async function getData(url) {
  try {
    let result = await Axios.get(
      `${process.env.REACT_APP_BACKEND_ONLINE_CATALOG_API}/web_catalogs/friendly_url/${url}`,
      {
        headers: {
          "Content-Type": "application/json",
          "x-api-key": process.env.REACT_APP_ONLINE_CATALOG_X_API_KEY,
        },
      }
    );
    return result.data;
  } catch (error) {
    console.log(`bigc-online-catalogs: ${error}`);
  }
}

export default function CatalogViewer({ setHideFooter }) {
  const { friendlyURL } = useParams();
  const { t, i18n } = useTranslation();
  const [sections, setSections] = useState([]);

  const [catalog, setCatalog] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [zoom, setZoom] = useState(1); // <- Zoom state
  const flipBook = useRef(); // <- Optional: future control
  const [currentPage, setCurrentPage] = useState(0); // <- เก็บหน้าปัจจุบัน
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === "undefined" ? 1024 : window.innerWidth,
    h: typeof window === "undefined" ? 768 : window.innerHeight,
  }));
  const [safeBottom, setSafeBottom] = useState(0);
  const scrollRef = useRef(null); // <- กล่อง scroll ที่ครอบ flipbook (ใช้ดัก pinch)
  const zoomRef = useRef(1); // <- อ่านค่า zoom ล่าสุดใน touch handler โดยไม่ผูก deps
  const jumpingRef = useRef(false); // <- turnToPage จากโค้ดก็ยิง onFlip ต้องแยกจากการพลิกของผู้ใช้
  const shiftRef = useRef(null); // <- กล่องที่เลื่อนเล่มให้ปกเดี่ยวอยู่กลางจอ (แก้ style ตรง ไม่ผ่าน state)
  const [responseStatus, setResponseStatus] = useState("");
  const [guideSeen, setGuideSeen] = useState(readGuideSeen);
  const [thumbsOpen, setThumbsOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const pageQuery = parseInt(searchParams.get("page") || "1", 10);
  const hideChrome =
    resolveDisplayMode(searchParams.get("mode"), catalog?.display_mode) ===
    DISPLAY_MODE_HIDE;
  const footerH = hideChrome ? 0 : FOOTER_H;
  const topBarH = hideChrome ? TOP_BAR_H : 0; // Mode 1 ไม่มีแถบหัว
  const toolbarGap = hideChrome ? TOOLBAR_GAP_EDGE : TOOLBAR_GAP_FOOTER;
  const reservedH = topBarH + TOOLBAR_H + toolbarGap + footerH;

  // ขนาดหน้ากระดาษที่คำนวณจากขนาดจอจริง แทนค่าคงที่ 350×500 / 400×570 แบบเดิม
  const bookSize = useMemo(
    () => calcBookSize(viewport.w, viewport.h, safeBottom, reservedH),
    [viewport.w, viewport.h, safeBottom, reservedH]
  );
  const isMobile = bookSize.portrait; // = แสดงหน้าเดียว

  const headers = useMemo(
    () => ({
      //Authorization: `Bearer ${authToken}`,
      "X-Client-Auth": process.env.REACT_APP_CLIENT_AUTH,
      //"x-user-id": userId,
      "Accept-Language": i18n.language.toUpperCase(),
    }),
    [i18n.language]
  );
  useEffect(() => {
    sessionStorage.setItem("previousPath", window.location.pathname);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      /*if (!authToken || !userId) {
        // console.log("Authentication token or user ID is missing.");
        return;
      }*/
      try {
        const pageId = await clientsThis(headers);

        const apiUrl = `${process.env.REACT_APP_API_URL}/api/pages/${pageId.root_page_id}`;
        const response = await Axios.get(apiUrl, {
          headers: headers,
        });
        const result = response.data?.data?.sections || [];
        await setSections(result);
      } catch (err) {
        console.error("Error fetching data:", err.message);
        //setError(err.message);
      }
    };

    fetchData();
  }, [i18n]);

  // โหลด Catalog (sync + reset)
  useEffect(() => {
    setCatalog(null);
    setHotspots([]);
    setResponseStatus("");
    const fetchCatalog = async () => {
      const getCatalog = await getData(friendlyURL);
      if (getCatalog && getCatalog.data) {
        setCatalog(getCatalog.data);
        setResponseStatus(getCatalog.status);
      } else {
        setCatalog(null);
        setResponseStatus("warning");
      }
    };
    fetchCatalog();
  }, [friendlyURL]);

  // โหลด Hotspot เมื่อ catalog เปลี่ยน
  useEffect(() => {
    if (!catalog?.id) {
      setHotspots([]);
      return;
    }
    const fetchHotspots = async () => {
      try {
        //console.log(catalog.id);

        let result = await Axios.get(
          `${process.env.REACT_APP_BACKEND_ONLINE_CATALOG_API}/web_hotspots/list?catalog_id=${catalog.id}`,
          {
            headers: {
              "x-api-key": process.env.REACT_APP_ONLINE_CATALOG_X_API_KEY,
            },
          }
        );
        //console.log(result.data)
        setHotspots(result.data || []);
      } catch (error) {
        setHotspots([]);
      }
    };
    fetchHotspots();
  }, [catalog]);

  useEffect(() => {
    setSafeBottom(readSafeBottom());
  }, []);

  // body ทั้งแอปมีพื้นหลังจุด (index.css) — หน้า catalog ใช้พื้นเรียบ แล้วคืนค่าเมื่อออกจากหน้า
  useEffect(() => {
    const prev = document.body.style.backgroundImage;
    document.body.style.backgroundImage = "none";
    return () => {
      document.body.style.backgroundImage = prev;
    };
  }, []);

  // footer อยู่ใน App.js — สั่งซ่อนตาม display_mode และคืนค่าเมื่อออกจากหน้านี้
  useEffect(() => {
    setHideFooter?.(hideChrome);
  }, [hideChrome, setHideFooter]);
  useEffect(() => () => setHideFooter?.(false), [setHideFooter]);

  useEffect(() => {
    let timer = null;
    const updateSize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setViewport((v) =>
          v.w === window.innerWidth && v.h === window.innerHeight
            ? v // ไม่เปลี่ยน = ไม่ re-render
            : { w: window.innerWidth, h: window.innerHeight }
        );
      }, 150);
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    window.addEventListener("orientationchange", updateSize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", updateSize);
      window.removeEventListener("orientationchange", updateSize);
    };
  }, []);

  const isZoomed = zoom > 1.001;

  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);

  // ย่อกลับ 100% แล้วให้เลื่อนกลับมาที่เดิม ไม่ค้างอยู่มุมใดมุมหนึ่ง
  useEffect(() => {
    if (isZoomed || !scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
    scrollRef.current.scrollTop = 0;
  }, [isZoomed]);

  // Pinch-to-zoom บนมือถือ — ของเดิมมีแต่ปุ่ม ผู้ใช้ส่วนใหญ่จึงไม่รู้ว่าซูมได้
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const gap = (t) =>
      Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    let pinch = null;

    const onStart = (e) => {
      if (e.touches.length < 2) return;
      pinch = { gap: gap(e.touches), zoom: zoomRef.current };
      // กันไม่ให้ page-flip ตีความสองนิ้วเป็นการเริ่มพลิกหน้า
      e.stopPropagation();
    };
    const onMove = (e) => {
      if (!pinch || e.touches.length < 2) return;
      e.stopPropagation();
      e.preventDefault();
      const next = pinch.zoom * (gap(e.touches) / pinch.gap);
      setZoom(Math.min(3, Math.max(1, next)));
    };
    const onEnd = (e) => {
      if (e.touches.length >= 2 || !pinch) return;
      const from = pinch.zoom;
      pinch = null;
      if (Math.abs(zoomRef.current - from) < 0.05) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event:
          zoomRef.current > from ? "catalog_zoom_in" : "catalog_zoom_out",
        catalog_id: friendlyURL,
        zoom_method: "pinch",
      });
    };

    el.addEventListener("touchstart", onStart, true);
    el.addEventListener("touchmove", onMove, { capture: true, passive: false });
    el.addEventListener("touchend", onEnd, true);
    el.addEventListener("touchcancel", onEnd, true);
    return () => {
      el.removeEventListener("touchstart", onStart, true);
      el.removeEventListener("touchmove", onMove, { capture: true });
      el.removeEventListener("touchend", onEnd, true);
      el.removeEventListener("touchcancel", onEnd, true);
    };
  }, [catalog, friendlyURL]);

  /* ตอนซูมอยู่: ลากเพื่อเลื่อนดูซ้าย-ขวา/บน-ล่าง
     - เมาส์/ทัชแพด → ลากด้วย pointer event (จับ scrollLeft/scrollTop เอง)
     - นิ้วบนมือถือ → ปล่อยให้เบราว์เซอร์ pan เองผ่าน overflow-auto (ลื่นกว่า มี momentum)
                      แต่ต้องปลดล็อก touch-action ที่ page-flip ตั้ง pan-y ไว้ ดูที่ className
     ทั้งสองทางต้องกันไม่ให้ page-flip ตีความการลากเป็นการพลิกหน้า */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const zoomedNow = () => zoomRef.current > 1.001;
    let drag = null;

    const blockFlipWhileZoomed = (e) => {
      if (zoomedNow()) e.stopPropagation();
    };

    const onPointerDown = (e) => {
      // ทัชปล่อยให้เบราว์เซอร์จัดการเอง / ลากด้วยปุ่มซ้ายเท่านั้น
      if (!zoomedNow() || e.pointerType === "touch" || e.button !== 0) return;
      drag = {
        x: e.clientX,
        y: e.clientY,
        left: el.scrollLeft,
        top: el.scrollTop,
      };
      try {
        el.setPointerCapture(e.pointerId);
      } catch {}
    };
    const onPointerMove = (e) => {
      if (!drag) return;
      e.preventDefault();
      el.scrollLeft = drag.left - (e.clientX - drag.x);
      el.scrollTop = drag.top - (e.clientY - drag.y);
    };
    const onPointerUp = (e) => {
      if (!drag) return;
      drag = null;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {}
    };

    el.addEventListener("mousedown", blockFlipWhileZoomed, true);
    el.addEventListener("touchstart", blockFlipWhileZoomed, true);
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);
    return () => {
      el.removeEventListener("mousedown", blockFlipWhileZoomed, true);
      el.removeEventListener("touchstart", blockFlipWhileZoomed, true);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [catalog]);

  const checkImageExists = async (url) => {
    try {
      const res = await fetch(url, { method: "HEAD" });
      return res.ok;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const pageIndex = pageQuery - 1;

    if (catalog?.pages && pageIndex >= 0 && pageIndex < catalog.pages) {
      const imageURL = `${catalog.image_paths}/page-${pageQuery}.jpg`;

      checkImageExists(imageURL).then((exists) => {
        if (!exists) {
          console.warn("❌ รูปภาพไม่พบ:", imageURL);
          return;
        }

        const timer = setTimeout(() => {
          requestAnimationFrame(() => {
            if (flipBook.current?.pageFlip) {
              const pf = flipBook.current.pageFlip();
              jumpingRef.current = true;
              pf.turnToPage(pageIndex); // flip(n) เลื่อนได้ทีละหน้า — ดู goToPage
              jumpingRef.current = false;
              setCurrentPage(pf.getCurrentPageIndex());
              //setCurrentPage(pageIndex);
            }
          });
        }, 500);
        return () => clearTimeout(timer);
      });
    }
  }, [catalog?.pages, pageQuery]);

  /*if (responseStatus === "warning") {
    return (
      <div style={{ width: "100%", maxWidth: 800, margin: "auto" }}>
        <div className="flex flex-col items-center mt-6">
          <div className="overflow-auto max-h-[90vh] rounded shadow-inner">
            <div className="flex justify-center w-full">
              <img
                src="/img/404.png"
                alt="404"
                style={{ width: 300, marginTop: 40 }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  } else if (responseStatus !== "success") {
    return (
      <div style={{ width: "100%", maxWidth: 800, margin: "auto" }}>
        <div className="flex flex-col items-center mt-6">
          <div
            className="overflow-auto max-h-[90vh] rounded shadow-inner flex justify-center items-center"
            style={{ minHeight: 400 }}
          >
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }
*/
  const handleGoToInputPage = (pageNum) => {
    const target = parseInt(pageNum, 10);
    if (!isNaN(target) && target >= 1 && target <= catalog.pages) {
      goToPage(target - 1);
    }
  };

  if (!catalog) return <div>Loading...</div>;

  const goToPage = (page) => {
    if (flipBook.current?.pageFlip) {
      // flip(n) ของ page-flip เวอร์ชันนี้เลื่อนได้ทีละหน้า (ไม่ถึงหน้าที่สั่ง) จึงใช้ turnToPage แทน
      const pf = flipBook.current.pageFlip();
      jumpingRef.current = true;
      pf.turnToPage(page);
      jumpingRef.current = false;
      setCurrentPage(pf.getCurrentPageIndex()); // หน้าคู่ = เลขหน้าซ้ายของคู่
      /*if (window.gtag) {
        window.gtag("event", "catalog_go_to_page", {
          event_category: "catalog_navigation",
          catalog_id: friendlyURL,
          value: page + 1, // บวก 1 เพราะ GA ใช้หน้าเริ่มที่ 1
        });
      }*/
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "catalog_go_to_page",
        catalog_id: friendlyURL,
        page_number: page + 1, // GA ใช้เลขหน้าเริ่มจาก 1
      });
    }
  };

  const nextPage = () => {
    flipBook.current?.pageFlip().flipNext();
    /*if (window.gtag) {
      window.gtag("event", "catalog_flip_next", {
        event_category: "catalog_navigation",
        catalog_id: friendlyURL,
        value: currentPage + 1, // บวก 1 เพราะ GA ใช้หน้าเริ่มที่ 1
      });
    }*/
    window.dataLayer.push({
      event: "catalog_flip_next",
      catalog_id: friendlyURL,
      page_number: currentPage + 1,
    });
  };

  const prevPage = () => {
    flipBook.current?.pageFlip().flipPrev();
    /*if (window.gtag) {
      window.gtag("event", "catalog_flip_prev", {
        event_category: "catalog_navigation",
        catalog_id: friendlyURL,
        value: currentPage + 1, // บวก 1 เพราะ GA ใช้หน้าเริ่มที่ 1
      });
    }*/
    window.dataLayer.push({
      event: "catalog_flip_prev",
      catalog_id: friendlyURL,
      page_number: currentPage + 1,
    });
  };

  const handleGoHome = () => {
    setZoom(1);
    goToPage(0);
    /*if (window.gtag) {
      window.gtag("event", "catalog_go_home", {
        event_category: "catalog_navigation",
        catalog_id: friendlyURL,
        value: 1,
      });
    }*/
    window.dataLayer.push({
      event: "catalog_go_home",
      catalog_id: friendlyURL,
    });
  };

  const dismissGuide = () => {
    if (guideSeen) return;
    setGuideSeen(true);
    try {
      localStorage.setItem(SWIPE_GUIDE_KEY, "1");
    } catch {}
  };

  // ปุ่มซูมปุ่มเดียว สลับ 1× ↔ 2× (pinch ยังซูมละเอียดได้เหมือนเดิม)
  const toggleZoom = () => {
    const zoomingIn = zoom <= 1.001;
    setZoom(zoomingIn ? 2 : 1);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: zoomingIn ? "catalog_zoom_in" : "catalog_zoom_out",
      catalog_id: friendlyURL,
      zoom_method: "button",
    });
  };

  const openThumbs = () => {
    setThumbsOpen(true);
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "catalog_open_thumbnails",
      catalog_id: friendlyURL,
      page_number: currentPage + 1,
    });
  };

  // หน้าที่เห็นอยู่ (หน้าคู่ = 2 หน้า ยกเว้นปกหน้า/ปกหลัง)
  const lastVisible =
    isMobile || currentPage === 0
      ? currentPage + 1
      : Math.min(currentPage + 2, catalog.pages);
  const atStart = currentPage === 0;
  const atEnd = lastVisible >= catalog.pages;
  const pageLabel =
    lastVisible > currentPage + 1
      ? `${currentPage + 1}–${lastVisible} / ${catalog.pages}`
      : `${currentPage + 1} / ${catalog.pages}`;
  const dateRange = [
    formatThaiDate(catalog.effective_start_date),
    formatThaiDate(catalog.effective_end_date),
  ]
    .filter(Boolean)
    .join(" – ");
  const showGuide = isMobile && !guideSeen;

  /* หน้าคู่: page-flip วางปกหน้าไว้ครึ่งขวา (ปกหลังเดี่ยวไว้ครึ่งซ้าย) ของพื้นที่ 2 หน้า
     เลื่อนเล่มครึ่งหน้าให้อยู่กลางจอ และกลับเป็นซ้าย-ขวาทันทีที่เริ่มพลิก
     ห้ามใช้ setState ระหว่างพลิก: react-pageflip เรียก updateFromHtml ทุกครั้งที่ render
     ทำให้หน้าเดิมเด้งกลับขึ้นมากลาง animation — onChangeState จึงแก้ style ผ่าน shiftRef ตรงๆ */
  const shiftFor = (index) => {
    if (isMobile) return 0;
    if (index === 0) return -bookSize.width / 2;
    if (catalog.pages % 2 === 0 && index === catalog.pages - 1)
      return bookSize.width / 2;
    return 0;
  };
  const handleChangeState = (e) => {
    const el = shiftRef.current;
    if (!el) return;
    if (e.data === "flipping") {
      el.style.transform = "translateX(0px)";
    } else if (e.data === "read") {
      // จบการพลิก (หรือปล่อยมือกลางทาง) — จัดตามหน้าที่อยู่จริง
      const index = flipBook.current?.pageFlip()?.getCurrentPageIndex() ?? 0;
      el.style.transform = `translateX(${shiftFor(index)}px)`;
    }
  };

  return (
    <div>
      {/* page-flip ฝัง .stf__parent{touch-action:pan-y} มากับไลบรารี ทำให้ปัดซ้าย-ขวาไม่ได้
          ตอนซูมต้องปลดล็อกเป็น pan-x pan-y ถึงจะเลื่อนดูด้านข้างได้
          catalog-hs = จุดกดสินค้า: ไม่มีกรอบ มีจุดเขียวกะพริบ ไฮไลต์ตอนชี้/แตะ */}
      <style>{`
        .catalog-pan .stf__parent{touch-action:pan-x pan-y !important}
        .catalog-hs{border-radius:10px;transition:background-color .2s,box-shadow .2s}
        .catalog-hs:hover,.catalog-hs:focus-visible,.catalog-hs:active{background:rgba(147,201,61,.16);box-shadow:inset 0 0 0 2px #93c93d;outline:none}
        .catalog-hs::after{content:"";position:absolute;right:6%;top:6%;width:14px;height:14px;border-radius:50%;background:#93c93d;border:2px solid #fff;animation:catalog-ping 2.2s ease-out infinite}
        @keyframes catalog-ping{0%{box-shadow:0 0 0 0 rgba(147,201,61,.7)}70%{box-shadow:0 0 0 12px rgba(147,201,61,0)}100%{box-shadow:0 0 0 0 rgba(147,201,61,0)}}
        @keyframes catalog-swipe{0%{transform:translateX(0) scale(.9);opacity:0}15%{transform:translateX(0) scale(1);opacity:1}70%{transform:translateX(-128px);opacity:1}100%{transform:translateX(-128px);opacity:0}}
        .catalog-finger{animation:catalog-swipe 1.9s ease-in-out infinite}
        @media (prefers-reduced-motion: reduce){.catalog-hs::after,.catalog-finger{animation:none}}
      `}</style>
      {/* แบนเนอร์กว้างเท่าหนังสือ (หน้าคู่ = 2 หน้า) ขอบซ้าย-ขวาจึงตรงกันทุกขนาดจอ
          รอโหลด catalog ก่อน เพื่อรู้ display_mode — ไม่งั้น Mode 2 จะเห็นแบนเนอร์แวบขึ้นมา */}
      {responseStatus !== "" && !hideChrome && (
        <BannerSection
          sections={sections}
          fitWidth={bookSize.portrait ? bookSize.width : bookSize.width * 2}
        />
      )}

      {/* 🔝 แถบหัว: ชื่อเล่ม + ช่วงวันที่ + ปุ่มกลับหน้าปก / ภาพย่อ
          เฉพาะ Mode 2 — Mode 1 มี banner อยู่แล้ว ให้เล่มต่อใต้ banner เลย (ภาพย่อยังเปิดจากเลขหน้าในแถบล่าง) */}
      {hideChrome && (
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-100">
          <div className="flex items-center gap-1.5 h-[52px] px-1.5 max-w-screen-xl mx-auto">
            <button
              onClick={handleGoHome}
              title="กลับหน้าแรกของเล่ม"
              aria-label="กลับหน้าแรกของเล่ม"
              className="grid place-items-center w-10 h-10 rounded-xl text-gray-800 hover:bg-[#f1f5ec] shrink-0"
            >
              <HomeIcon className="h-[22px] w-[22px]" />
            </button>
            <div className="flex-1 min-w-0 leading-tight">
              <div className="text-sm font-semibold truncate">{catalog.name}</div>
              {dateRange && (
                <div className="text-[11px] text-gray-500">{dateRange}</div>
              )}
            </div>
            <button
              onClick={openThumbs}
              title="ภาพย่อทุกหน้า"
              aria-label="ภาพย่อทุกหน้า"
              className="grid place-items-center w-10 h-10 rounded-xl text-gray-800 hover:bg-[#f1f5ec] shrink-0"
            >
              <Squares2X2Icon className="h-[22px] w-[22px]" />
            </button>
          </div>
          <div className="h-[3px] bg-[#eef0ec]">
            <div
              className="h-full bg-[#93c93d] rounded-r transition-[width] duration-300"
              style={{ width: `${(lastVisible / catalog.pages) * 100}%` }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          width: "100%",
          // เดิมล็อกไว้ 850 ทำให้จอใหญ่แค่ไหนก็ได้หนังสือกว้างเท่าเดิม
          maxWidth: bookSize.portrait
            ? "100%"
            : bookSize.width * 2 + bookSize.pad * 2 + 24,
          margin: "auto",
        }}
        className="overflow-visible"
      >
        <div className="flex flex-col items-center pt-3 overflow-visible">
          {/* 🖼️ Flipbook (zoom ถูก apply แค่ตรงนี้) */}
          <div
            ref={scrollRef}
            /* ตอนซูม: ปลดล็อก touch-action ของ .stf__parent (page-flip ตั้ง pan-y ไว้
               ทำให้ปัดซ้าย-ขวาไม่ได้) + เปลี่ยนเคอร์เซอร์เป็นมือลาก */
            className={
              isZoomed
                ? "w-full overflow-auto catalog-pan cursor-grab active:cursor-grabbing select-none"
                : "w-full overflow-auto"
            }
            style={{
              maxHeight: `calc(100vh - ${footerH + topBarH}px)`,
              paddingBottom: TOOLBAR_H + toolbarGap, // เว้นที่ให้แถบปุ่มลอย ไม่ทับท้ายเล่ม
            }}
          >
            <div
              style={{
                zoom: zoom, // ✅ ใช้ zoom แทน transform
              }}
              className="w-fit mx-auto"
            >
              <div
                ref={shiftRef}
                className="relative transition-transform duration-500 ease-out"
                style={{
                  marginLeft: bookSize.pad,
                  marginRight: bookSize.pad,
                  transform: `translateX(${shiftFor(currentPage)}px)`,
                }}
              >
                <div>
                  <HTMLFlipBook
                    /* react-pageflip อ่าน width/height แค่ตอน mount ครั้งแรก
                       (มี guard `!pageFlip.current` ใน build/index.es.js)
                       ถ้าไม่บังคับ remount ด้วย key ขนาดจะไม่เปลี่ยนตอนหมุนจอ */
                    key={`${bookSize.width}x${bookSize.height}-${bookSize.portrait}`}
                    ref={flipBook}
                    width={bookSize.width}
                    height={bookSize.height}
                    startPage={currentPage}
                    showCover={true}
                    drawShadow={true}
                    usePortrait={bookSize.portrait}
                    mobileScrollSupport={true}
                    onChangeState={handleChangeState}
                    onFlip={(e) => {
                      setCurrentPage(e.data);
                      // ผู้ใช้พลิกเองได้แล้ว = รู้วิธีแล้ว (ไม่นับการกระโดดหน้าจากโค้ด)
                      if (!jumpingRef.current) dismissGuide();
                      window.dataLayer = window.dataLayer || [];
                      window.dataLayer.push({
                        event: "catalog_view_page",
                        catalog_id: friendlyURL,
                        page_number: e.data + 1, // GA นับจากหน้า 1
                      });
                    }}
                  >
                    {Array.from({ length: catalog.pages }).map((_, i) => {
                      const page = i + 1;
                      const isVisible =
                        page - 1 >= currentPage && page - 1 <= currentPage + 4;

                      const pageHotspots = hotspots.filter(
                        (h) => h.page === page
                      );

                      return (
                        <div
                          key={page}
                          className="relative shadow-[0_2px_6px_rgba(0,0,0,.08),0_18px_40px_-12px_rgba(0,0,0,.28)]"
                        >
                          {isVisible ? (
                            <LazyImage
                              src={`${catalog.image_paths}/page-${page}.jpg`}
                              alt={`Page ${page}`}
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-100" />
                          )}

                          {isVisible &&
                            pageHotspots.map((h, idx) => (
                              <a
                                key={idx}
                                href={h.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`ลิงก์สินค้า หน้า ${page}`}
                                className="catalog-hs absolute cursor-pointer z-10"
                                style={{
                                  left: `${h.x * 100}%`,
                                  top: `${h.y * 100}%`,
                                  width: `${h.width * 100}%`,
                                  height: `${h.height * 100}%`,
                                }}
                                onClick={() => {
                                  window.dataLayer = window.dataLayer || [];
                                  window.dataLayer.push({
                                    event: "catalog_click_hotspot",
                                    catalog_id: friendlyURL,
                                    page_number: page,
                                    link_url: h.link,
                                  });
                                }}
                              />
                            ))}
                        </div>
                      );
                    })}
                  </HTMLFlipBook>
                </div>

                {/* ปุ่มกลมพลิกหน้า: จอใหญ่อยู่นอกเล่ม / มือถือลอยทับขอบเล่ม */}
                <button
                  onClick={prevPage}
                  aria-label="หน้าก่อน"
                  className={`absolute top-1/2 -translate-y-1/2 z-30 grid place-items-center rounded-full text-gray-800 transition-opacity ${
                    isMobile
                      ? "left-1.5 w-10 h-10 bg-white/90 shadow-[0_2px_12px_rgba(0,0,0,.22)]"
                      : "-left-[60px] w-11 h-11 bg-white shadow-[0_2px_10px_rgba(0,0,0,.14)] hover:scale-105"
                  } ${atStart ? "opacity-0 pointer-events-none" : ""}`}
                >
                  <ChevronLeftIcon className="h-[22px] w-[22px] stroke-2" />
                </button>
                <button
                  onClick={nextPage}
                  aria-label="หน้าถัดไป"
                  className={`absolute top-1/2 -translate-y-1/2 z-30 grid place-items-center rounded-full text-gray-800 transition-opacity ${
                    isMobile
                      ? "right-1.5 w-10 h-10 bg-white/90 shadow-[0_2px_12px_rgba(0,0,0,.22)]"
                      : "-right-[60px] w-11 h-11 bg-white shadow-[0_2px_10px_rgba(0,0,0,.14)] hover:scale-105"
                  } ${atEnd ? "opacity-0 pointer-events-none" : ""}`}
                >
                  <ChevronRightIcon className="h-[22px] w-[22px] stroke-2" />
                </button>

                {/* 👆 หน้าแนะนำการปัด — มือถือ, แสดงครั้งเดียวต่อเครื่อง */}
                {showGuide && (
                  <div
                    role="dialog"
                    aria-label="วิธีเปลี่ยนหน้า"
                    onClick={dismissGuide}
                    className="absolute inset-0 z-[35] flex flex-col items-center justify-start gap-3.5 px-4 pt-[14%] text-center text-white bg-[rgba(10,14,11,.58)] cursor-pointer"
                  >
                    <div className="relative w-40 h-11" aria-hidden="true">
                      <div className="absolute left-2 right-2 top-[21px] h-0.5 rounded bg-gradient-to-r from-white/70 to-white/0" />
                      <span className="catalog-finger absolute top-1.5 right-0 w-8 h-8 rounded-full bg-white/95 shadow-[0_0_0_7px_rgba(255,255,255,.22)]" />
                    </div>
                    <b className="text-[17px] font-semibold leading-snug">
                      ปัดซ้าย-ขวาเพื่อพลิกหน้า
                    </b>
                    <small className="text-[13px] leading-relaxed opacity-90">
                      หรือแตะปุ่ม ‹ › ที่ขอบเล่ม
                      <br />
                      แตะเลขหน้าด้านล่างเพื่อดูทุกหน้า
                    </small>
                    <button
                      onClick={dismissGuide}
                      className="mt-1 px-[22px] py-2.5 rounded-full bg-[#93c93d] text-[#16300a] text-sm font-semibold"
                    >
                      เข้าใจแล้ว
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🎛️ แถบปุ่มลอยเหนือ footer (Mode 2 ไม่มี footer → ชิดขอบล่าง) */}
      <div
        className="fixed left-1/2 -translate-x-1/2 z-40 flex items-center gap-0.5 p-1 bg-white/95 backdrop-blur rounded-full shadow-[0_1px_3px_rgba(0,0,0,.08),0_8px_24px_rgba(0,0,0,.14)] whitespace-nowrap"
        style={{
          bottom: `calc(${footerH + toolbarGap}px + env(safe-area-inset-bottom))`,
        }}
      >
        <button
          onClick={prevPage}
          disabled={atStart}
          aria-label="หน้าก่อน"
          className="grid place-items-center w-11 h-11 rounded-full text-gray-800 hover:bg-[#f1f5ec] disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          <ChevronLeftIcon className="h-[22px] w-[22px] stroke-2" />
        </button>
        <button
          onClick={openThumbs}
          aria-label="เลือกหน้า"
          className="flex items-center gap-1.5 h-9 px-3.5 rounded-full bg-[#f1f5ec] text-gray-800 text-[13px] font-medium tabular-nums"
        >
          <Squares2X2Icon className="h-3.5 w-3.5 text-[#4f7d12]" />
          {pageLabel}
        </button>
        <button
          onClick={nextPage}
          disabled={atEnd}
          aria-label="หน้าถัดไป"
          className="grid place-items-center w-11 h-11 rounded-full text-gray-800 hover:bg-[#f1f5ec] disabled:text-gray-300 disabled:hover:bg-transparent"
        >
          <ChevronRightIcon className="h-[22px] w-[22px] stroke-2" />
        </button>
        <span className="w-px h-[22px] bg-gray-200 mx-0.5" />
        <button
          onClick={toggleZoom}
          aria-label={isZoomed ? "ซูมออก" : "ซูมเข้า"}
          className={`grid place-items-center w-11 h-11 rounded-full ${
            isZoomed
              ? "bg-[#93c93d] text-[#16300a]"
              : "text-gray-800 hover:bg-[#f1f5ec]"
          }`}
        >
          {isZoomed ? (
            <MagnifyingGlassMinusIcon className="h-[22px] w-[22px]" />
          ) : (
            <MagnifyingGlassPlusIcon className="h-[22px] w-[22px]" />
          )}
        </button>
      </div>

      {/* 🗂️ ภาพย่อทุกหน้า (bottom sheet) — รูปโหลดแบบ lazy เฉพาะตอนเปิด */}
      {thumbsOpen && (
        <div
          className="fixed inset-0 z-[60] flex flex-col justify-end bg-[rgba(10,14,11,.42)]"
          onClick={(e) => {
            if (e.target === e.currentTarget) setThumbsOpen(false);
          }}
        >
          <div
            className="bg-white rounded-t-[18px] px-3.5 pt-2.5 max-h-[78vh] overflow-auto w-full max-w-screen-md mx-auto"
            style={{ paddingBottom: "calc(18px + env(safe-area-inset-bottom))" }}
          >
            <div className="w-10 h-1 rounded bg-gray-300 mx-auto mb-2.5" />
            <div className="flex items-center justify-between mb-2.5">
              <h3 className="text-[15px] font-semibold">
                ทุกหน้า ({catalog.pages})
              </h3>
              <button
                onClick={() => setThumbsOpen(false)}
                aria-label="ปิด"
                className="grid place-items-center w-8 h-8 rounded-xl hover:bg-[#f1f5ec]"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            <div className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(84px,1fr))]">
              {Array.from({ length: catalog.pages }).map((_, i) => {
                const current = i >= currentPage && i < lastVisible;
                return (
                  <button
                    key={i}
                    onClick={() => {
                      goToPage(i);
                      setThumbsOpen(false);
                    }}
                    className={`flex flex-col items-center gap-1 text-[11px] tabular-nums ${
                      current
                        ? "text-[#4f7d12] font-semibold"
                        : "text-gray-500"
                    }`}
                  >
                    <img
                      src={`${catalog.image_paths}/page-${i + 1}.jpg`}
                      alt={`หน้า ${i + 1}`}
                      loading="lazy"
                      /* ใช้ shadow แทน ring-* เพราะ muteluloading.css ประกาศ .ring-1 เป็น spinner (หมุน) */
                      className={`w-full aspect-[594/846] object-cover rounded bg-gray-100 ${
                        current
                          ? "shadow-[0_0_0_3px_#93c93d]"
                          : "shadow-[0_0_0_1px_#e5e7eb]"
                      }`}
                    />
                    หน้า {i + 1}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
